<!-- eslint-disable vue/no-parsing-error -->
<template>
  <ai-booking-bar
    ref="bookingBar"
    v-model:dates="searchStore.dates"
    v-model:include-flights="searchStore.includeFlights"
    v-model:rooms-and-guests="searchStore.rooms"
    :disabled="searchStore.loading"
    :manage-flights="isFlightHotelAvailable"
    :mobile-items="mobileItems"
    :mobile-titles="{
      origins: $t('homepage.hero.bar.mobile.origin.title'),
      destinations: $t('homepage.hero.bar.mobile.destination.title'),
    }"
    :rooms-and-guests-errors="childrenAgeMobileError"
    slim
    @reset-all="reset">
    <template #input-before>
      <origin-autocomplete
        v-if="isFlightHotelAvailable && searchStore.includeFlights"
        ref="originField"
        v-model="searchStore.origin"
        :errors="originError" />

      <destination-autocomplete
        v-model="searchStore.destination"
        :disabled="searchStore.loading"
        @keydown.enter="submit" />
    </template>

    <template #action>
      <ai-button
        :label="$t('homepage.hero.bar.submit')"
        :loading="searchStore.loading"
        @click="submit" />
    </template>

    <!-- Mobile -->
    <template #mobile-default-before="slotProps">
      <ai-field-input
        v-if="isFlightHotelAvailable && searchStore.includeFlights"
        :label="$t('homepage.hero.bar.mobile.origin.label')"
        :errors="originError"
        :model-value="searchStore.origin.text"
        :placeholder="$t('homepage.hero.bar.mobile.origin.placeholder')"
        class="SearchBookingBar-mobileInput"
        name="origin-mobile"
        readonly
        @click="slotProps.setPage('origins'); delayedFocusTo('leaving-from');"
        @keyup.enter="slotProps.setPage('origins'); delayedFocusTo('leaving-from');">
        <template #right>
          <ai-icon
            :size="18"
            class="AiBookingMobileForm-icon"
            name="location" />
        </template>
      </ai-field-input>

      <ai-field-input
        :label="$t('homepage.hero.bar.mobile.destination.label')"
        :model-value="searchStore.destination?.text ?? ''"
        :placeholder="$t('homepage.hero.bar.mobile.destination.placeholder')"
        class="SearchBookingBar-mobileInput"
        readonly
        @click="slotProps.setPage('destinations'); delayedFocusTo('destination');"
        @keyup.enter.prevent="slotProps.setPage('destinations'); delayedFocusTo('destination');">
        <template #right>
          <ai-icon
            :size="18"
            class="AiBookingMobileForm-icon"
            name="location" />
        </template>
      </ai-field-input>
    </template>

    <!-- Mobile - Destination page -->
    <template #origins>
      <origin-autocomplete
        v-model="searchStore.origin"
        class="SearchBookingBar-mobilePage-input"
        hide-suggestions
        v-if="isFlightHotelAvailable"
        :errors="originError"
        @origins-fetched="origins => (originSuggestions = origins)" />

      <origin-suggestion
        v-for="(suggestion, index) of originSuggestions"
        :id="suggestion.id"
        :key="`${suggestion.type}-${index}`"
        :country="suggestion.country"
        :full-name="suggestion.fullName"
        :name="suggestion.name"
        :type="suggestion.type"
        @click="setOriginSuggestion" />
    </template>

    <template #destinations>
      <destination-autocomplete
        v-model="searchStore.destination"
        :input-hint="$t('search.filter.destination.helper')"
        class="SearchBookingBar-mobilePage-input"
        hide-suggestions
        @destinations-fetched="
          destinations => (destinationSuggestions = destinations)
        " />

      <template v-if="destinationSuggestions.length > 0">
        <destination-suggestion
          v-for="(suggestion, index) of destinationSuggestions"
          :key="`${suggestion.type}-${index}`"
          :city="suggestion.city"
          :country="suggestion.country"
          :hotel="suggestion.hotel"
          :type="suggestion.type"
          @click="setSuggestion" />
      </template>
      <div v-else class="SearchBookingBar-emptyResults">
        <ai-typo as="p" color="stratos-900" variant="legal-bold">
          {{ $t('search.filter.destination.emptyResults') }}
        </ai-typo>
        <ai-typo as="p" color="stratos-700" variant="legal-regular">
          {{ $t('search.filter.destination.emptyResultsHelper') }}
        </ai-typo>
      </div>
    </template>

    <template #mobile-submit>
      <ai-button
        :label="$t('homepage.hero.bar.mobile.submit')"
        :loading="searchStore.loading && !searchStore.fetchError"
        @click="submit" />
    </template>
  </ai-booking-bar>
</template>

<script lang="ts" setup>
import { useApolloClient } from '@vue/apollo-composable';

import { useBookingStore } from '~/domains/booking';
import {
  useFlightHotelAvailability,
  useFlightHotelValidator,
} from '~/domains/feature';
import type {
  SearchDestination as GQLSearchDestination,
  SearchOrigin as GQLSearchOrigin,
} from '~/domains/graphql';
import { SearchDestinationResultType } from '~/domains/graphql';
import type { SearchDestination } from '~/domains/search';
import { useSearchStore } from '~/domains/search';
import type { Props as DestinationSuggestionProps } from '~/domains/search/components/DestinationSuggestion.vue';
import type { Props as OriginSuggestionProps } from '~/domains/search/components/OriginSuggestion.vue';
import type { MobileItem } from '~/domains/ux/organisms/AiBookingBar/AiBookingBar.vue';
import { event, formatDate, delayedFocusTo } from '~/helpers';

import type DestinationAutocomplete from '../DestinationAutocomplete.vue';
import type OriginAutocomplete from '../OriginAutocomplete.vue';

const bookingBar = ref();

const router = useRouter();
const searchStore = useSearchStore();
const bookingStore = useBookingStore();
const { t } = useI18n();
const { isFlightHotelAvailable } = useFlightHotelAvailability();
const { originError, validateFlightHotelParams } = useFlightHotelValidator();

const destinationSuggestions = ref<GQLSearchDestination[]>([]);

const originSuggestions = ref<GQLSearchOrigin[]>([]);

const originField = ref<typeof OriginAutocomplete>();

const mobileDestinationLabel = computed(() =>
  searchStore.destination?.text === ''
    ? t('booking.bar.mobile.allDestinations')
    : searchStore.destination?.text,
);

const mobileDestinationIsBold = computed(
  () =>
    mobileDestinationLabel?.value !== t('booking.bar.mobile.allDestinations'),
);

const mobileOriginLabel = computed(() =>
  searchStore.origin.text === ''
    ? t('booking.bar.mobile.selectOrigin')
    : searchStore.origin.text,
);

const mobileOriginIsBold = computed(
  () => mobileOriginLabel?.value !== t('booking.bar.mobile.selectOrigin'),
);

const mobileItems = computed<MobileItem[]>(() => {
  const destinationItem = {
    icon: 'location',
    key: 'all-destination',
    label: mobileDestinationLabel.value,
    position: 'before',
    bold: mobileDestinationIsBold.value,
  } as MobileItem;

  const originItem = {
    icon: 'location',
    key: 'origin',
    label: mobileOriginLabel.value,
    position: 'before',
    bold: mobileOriginIsBold.value,
  } as MobileItem;

  return searchStore.includeFlights
    ? [originItem, destinationItem]
    : [destinationItem];
});

const setSuggestion = (suggestion: DestinationSuggestionProps) => {
  const searchValue: SearchDestination = {
    text: '',
    type: suggestion.type as SearchDestinationResultType,
  };

  switch (suggestion?.type) {
    case SearchDestinationResultType.Country:
      searchValue.text = suggestion.country as string;
      break;
    case SearchDestinationResultType.City:
      searchValue.text = suggestion.city as string;
      break;
    case SearchDestinationResultType.Hotel:
      searchValue.text = suggestion.hotel?.name as string;
      searchValue.ridcode = suggestion.hotel?.id as string;
      break;
  }

  searchStore.destination = searchValue;

  if (typeof bookingBar.value.setPage === 'function') {
    bookingBar.value.setPage(undefined);
  }
};

const setOriginSuggestion = (suggestion: OriginSuggestionProps) => {
  searchStore.origin = {
    text: suggestion.name,
    type: suggestion.type,
  };

  if (typeof bookingBar.value.setPage === 'function') {
    bookingBar.value.setPage(undefined);
  }
};

const { client } = useApolloClient();

const childrenAgeMobileError = ref<
  { childrenAges: number[]; roomIndex: number }[]
>([]);

const reset = () => {
  searchStore.origin.text = '';
  searchStore.destination.text = '';
  childrenAgeMobileError.value = [];
  searchStore.includeFlights = false;
};

const addSearchParamsInUrl = () => {
  const query: Record<string, string> = {};

  if (searchStore.dates.length) {
    query.dates = JSON.stringify(
      searchStore.dates.map(date => formatDate(date, 'yyyy-MM-dd')),
    );
  }

  if (searchStore.destination.text || searchStore.destination.type) {
    query.destination = JSON.stringify(searchStore.destination);
  }

  if (searchStore.rooms.length) {
    query.roomsAndGuests = JSON.stringify(searchStore.rooms);
  }

  if (
    searchStore.includeFlights &&
    (searchStore.origin.text || searchStore.origin.type)
  ) {
    query.origin = JSON.stringify(searchStore.origin);
  }

  if (searchStore.includeFlights && isFlightHotelAvailable) {
    query.includeFlights = 'true';
  }

  router.replace({
    ...router.currentRoute.value,
    query,
  });
};

const submit = async () => {
  childrenAgeMobileError.value = [];
  if (
    searchStore.rooms.some(room =>
      room.childrenAges.some(age => typeof age !== 'number'),
    )
  ) {
    for (let roomIndex = 0; roomIndex < searchStore.rooms.length; roomIndex++) {
      const room = searchStore.rooms[roomIndex];
      const roomError = { roomIndex, childrenAges: [] } as {
        roomIndex: number;
        childrenAges: number[];
      };

      for (let i = 0; i < room.childrenAges.length; i++) {
        if (typeof room.childrenAges[i] === 'number') continue;

        roomError.childrenAges.push(i);
      }

      childrenAgeMobileError.value.push(roomError);
    }

    return;
  }

  if (searchStore.includeFlights && isFlightHotelAvailable) {
    validateFlightHotelParams(undefined, undefined, searchStore.origin);

    if (originError.value) {
      return;
    }
  }

  await searchStore.searchHotels(client);

  bookingBar.value?.closeMobileForm?.();

  addSearchParamsInUrl();

  event('submit_booking_form', {
    category: 'search',
    action: 'search - click on cta',
    label: 'search - hotel',
    adults_nb: bookingStore.nbAdults,
    arrival_date: formatDate(searchStore.dates[0], 'dd-MM-yyyy'),
    children_age1: bookingStore.remainingGuests[0]?.childrenAges,
    children_nb: bookingStore.remainingGuests[0]?.childrenAges.length,
    day_before_arrival: searchStore.currentSearchVariables.dates?.nights,
    departure_date: formatDate(searchStore.dates[1], 'dd-MM-yyyy'),
    lead_time: searchStore.currentSearchVariables.dates?.dateIn,
    night_nb: searchStore.currentSearchVariables.dates?.nights,
    room_nb: searchStore.rooms.length,
    destination: '',
    destination_city: '',
    destination_city_code: '',
    destination_country_code: '',
    destination_country_name: '',
    hotel_brand_code: '',
    hotel_name: '',
    hotel_rid_code: '',
    accomodation_type: '',
    booking_form_type: '',
    child_conversion_status: '',
    offer_code: '',
    client_type: '',
  });
};

const focusOriginField = () => {
  if (!originField.value) return;

  originField.value.input.focus();
};

const openDatepicker = () => {
  if (!bookingBar.value) return;

  bookingBar.value.openDatepicker();
};

defineExpose({ focusOriginField, openDatepicker });
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/constants';

.SearchBookingBar-mobileInput {
  margin-bottom: constants.$margin-04;
}

.SearchBookingBar-mobilePage-input {
  margin-bottom: constants.$margin-02;

  :deep(.AiFieldInput-input) {
    @supports (-webkit-touch-callout: none) {
      font-size: 1.6rem;
    }
  }
}

.SearchBookingBar-emptyResults {
  text-align: center;
}
</style>
